import type { StrategyId } from "../model.ts";
import type { RetentionState } from "./fact-mastery.ts";

export const STRATEGY_MASTERY_STAGES = [
  "UNSEEN",
  "INTRODUCED",
  "GUIDED",
  "INDEPENDENT",
  "STABLE",
] as const;
export type StrategyMasteryStage = (typeof STRATEGY_MASTERY_STAGES)[number];

export const STRATEGY_EVIDENCE_KINDS = [
  "INTRODUCTION_COMPLETED",
  "GUIDED_SUCCESS",
  "INDEPENDENT_SUCCESS",
  "INDEPENDENT_FAILURE",
  "REFRESH_COMPLETED",
] as const;
export type StrategyEvidenceKind = (typeof STRATEGY_EVIDENCE_KINDS)[number];

export interface StrategyEvidence {
  readonly id: string;
  readonly profileId: string;
  readonly strategyId: StrategyId;
  readonly sessionId: string;
  readonly occurredAt: string;
  readonly kind: StrategyEvidenceKind;
  readonly presentationId: string | null;
  readonly attemptId: string | null;
}

export interface StrategyMasteryRules {
  readonly version: string;
  readonly stableSuccessesRequired: number;
  readonly stableSessionCountRequired: number;
  readonly retentionGapMs: number;
  readonly maintenanceDueMs: number;
  readonly repeatedFailureCount: number;
}

export const DEFAULT_STRATEGY_MASTERY_RULES: StrategyMasteryRules = {
  version: "strategy-mastery-v1",
  stableSuccessesRequired: 3,
  stableSessionCountRequired: 2,
  retentionGapMs: 24 * 60 * 60 * 1000,
  maintenanceDueMs: 14 * 24 * 60 * 60 * 1000,
  repeatedFailureCount: 2,
};

export interface StrategyMastery {
  readonly strategyId: StrategyId;
  readonly currentStage: StrategyMasteryStage;
  readonly highestStageEver: StrategyMasteryStage;
  readonly retentionState: RetentionState;
  readonly refreshDue: boolean;
  readonly introducedAt: string | null;
  readonly guidedSuccesses: number;
  readonly independentSuccesses: number;
  readonly recentFailures: number;
  readonly lastUsedAt: string | null;
  readonly stableAt: string | null;
  readonly rulesetVersion: string;
}

function stageRank(stage: StrategyMasteryStage): number {
  return STRATEGY_MASTERY_STAGES.indexOf(stage);
}

function maxStage(
  a: StrategyMasteryStage,
  b: StrategyMasteryStage,
): StrategyMasteryStage {
  return stageRank(a) >= stageRank(b) ? a : b;
}

function hasRetentionGap(
  evidence: readonly StrategyEvidence[],
  requiredGapMs: number,
): boolean {
  for (let index = 1; index < evidence.length; index += 1) {
    if (
      Date.parse(evidence[index].occurredAt) - Date.parse(evidence[index - 1].occurredAt)
      >= requiredGapMs
    ) {
      return true;
    }
  }
  return false;
}

function rawStage(
  evidence: readonly StrategyEvidence[],
  rules: StrategyMasteryRules,
): StrategyMasteryStage {
  let stage: StrategyMasteryStage = "UNSEEN";
  if (evidence.some((item) => item.kind === "INTRODUCTION_COMPLETED")) {
    stage = "INTRODUCED";
  }
  if (evidence.some((item) => item.kind === "GUIDED_SUCCESS")) {
    stage = "GUIDED";
  }

  const independentSuccesses = evidence.filter(
    (item) => item.kind === "INDEPENDENT_SUCCESS",
  );
  if (independentSuccesses.length > 0) {
    stage = "INDEPENDENT";
  }
  if (
    independentSuccesses.length >= rules.stableSuccessesRequired
    && new Set(independentSuccesses.map((item) => item.sessionId)).size
      >= rules.stableSessionCountRequired
    && hasRetentionGap(independentSuccesses, rules.retentionGapMs)
  ) {
    stage = "STABLE";
  }
  return stage;
}

export function evaluateStrategyMastery(
  strategyId: StrategyId,
  allEvidence: readonly StrategyEvidence[],
  now: string,
  rules: StrategyMasteryRules = DEFAULT_STRATEGY_MASTERY_RULES,
): StrategyMastery {
  const evidence = allEvidence
    .filter((item) => item.strategyId === strategyId)
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));

  let highestStageEver: StrategyMasteryStage = "UNSEEN";
  let stableAt: string | null = null;
  for (let index = 1; index <= evidence.length; index += 1) {
    const stage = rawStage(evidence.slice(0, index), rules);
    highestStageEver = maxStage(highestStageEver, stage);
    if (stage === "STABLE" && stableAt === null) {
      stableAt = evidence[index - 1].occurredAt;
    }
  }

  const relevantTail = evidence
    .filter((item) =>
      item.kind === "INDEPENDENT_SUCCESS"
      || item.kind === "INDEPENDENT_FAILURE"
    );
  let consecutiveFailures = 0;
  for (let index = relevantTail.length - 1; index >= 0; index -= 1) {
    if (relevantTail[index].kind !== "INDEPENDENT_FAILURE") break;
    consecutiveFailures += 1;
  }

  let currentStage = rawStage(evidence, rules);
  let refreshDue = false;
  if (highestStageEver === "STABLE" && consecutiveFailures > 0) {
    refreshDue = true;
    currentStage = consecutiveFailures < rules.repeatedFailureCount
      ? "STABLE"
      : "INDEPENDENT";
  }

  const lastUsed = evidence.at(-1) ?? null;
  const lastIndependentSuccess = [...evidence]
    .reverse()
    .find((item) => item.kind === "INDEPENDENT_SUCCESS") ?? null;

  let retentionState: RetentionState = "NOT_DUE";
  if (refreshDue) {
    retentionState = "REFRESH_DUE";
  } else if (highestStageEver === "STABLE" && lastIndependentSuccess) {
    retentionState = Date.parse(now) - Date.parse(lastIndependentSuccess.occurredAt)
      >= rules.maintenanceDueMs
      ? "DUE"
      : "STABLE";
  }

  return {
    strategyId,
    currentStage,
    highestStageEver,
    retentionState,
    refreshDue,
    introducedAt: evidence.find((item) => item.kind === "INTRODUCTION_COMPLETED")?.occurredAt ?? null,
    guidedSuccesses: evidence.filter((item) => item.kind === "GUIDED_SUCCESS").length,
    independentSuccesses: evidence.filter((item) => item.kind === "INDEPENDENT_SUCCESS").length,
    recentFailures: consecutiveFailures,
    lastUsedAt: lastUsed?.occurredAt ?? null,
    stableAt,
    rulesetVersion: rules.version,
  };
}
